<script setup lang="ts">
/// <reference types="@types/google.maps" />
import { setOptions, importLibrary } from '@googlemaps/js-api-loader'
import type { ComponentPublicInstance, Component } from 'vue'

interface GoogleMapsMarker {
    position: { lat: number; lng: number }
    infoWindow: {
        component: Component
        props: Record<string, unknown>
    }
    onClick(): void
}

const props = withDefaults(defineProps<{
    mapCenter?: { lat: number; lng: number }
    mapZoom?: number
    markers?: GoogleMapsMarker[]
    mapId?: string
    selectMarker?: number | null
}>(), {
    mapCenter: () => ({ lat: 51, lng: 9 }),
    mapZoom: 6,
    markers: () => ([]),
    mapId: 'DEMO_MAP_ID',
    selectMarker: null
})

const emits = defineEmits<{
    loaded: [value: boolean]
}>()

const { googleMapsPlacesApiKey } = useRuntimeConfig().public

if (import.meta.client && typeof googleMapsPlacesApiKey === 'string' && googleMapsPlacesApiKey) {
    setOptions({ key: googleMapsPlacesApiKey })
}

const uuid = useId()
const mapElId = `map-${uuid}`
const windowInfoElements = ref<ComponentPublicInstance[]>([])

let mapElement: google.maps.Map
let InfoWindow: google.maps.MapsLibrary['InfoWindow']
let mapInfoWindow: google.maps.InfoWindow
let AdvancedMarkerElement: google.maps.MarkerLibrary['AdvancedMarkerElement']
let PinElement: google.maps.MarkerLibrary['PinElement']
let mapMarkers: google.maps.marker.AdvancedMarkerElement[] = []
let bounds: google.maps.LatLngBounds

onMounted(async () => {
    const mapsLib = await importLibrary('maps') as google.maps.MapsLibrary
    const markerLib = await importLibrary('marker') as google.maps.MarkerLibrary

    InfoWindow = mapsLib.InfoWindow
    AdvancedMarkerElement = markerLib.AdvancedMarkerElement
    PinElement = markerLib.PinElement

    mapElement = new mapsLib.Map(document.getElementById(mapElId) as HTMLElement, {
        center: props.mapCenter,
        zoom: props.mapZoom,
        mapId: props.mapId,
    })

    if (props.markers.length > 0) {
        renderMarkers(props.markers)
    }

    emits('loaded', true)
})

watch(() => props.markers, (value) => {
    renderMarkers(value)
}, { deep: true })

watch(() => props.selectMarker, (markerIndex: number | null) => {
    if (markerIndex == null || mapMarkers[markerIndex]?.position == null) return

    mapInfoWindow.close()

    const b = new google.maps.LatLngBounds()
    b.extend(mapMarkers[markerIndex]!.position as google.maps.LatLng)
    mapElement.fitBounds(b)
    mapElement.setZoom(17)
})

function renderMarkers(value: GoogleMapsMarker[]): void {
    bounds = new google.maps.LatLngBounds()
    mapMarkers.forEach(m => { m.map = null })
    mapInfoWindow = new InfoWindow()

    mapMarkers = value.map((data, i) => {
        const marker = setMarker(data, i)
        bounds.extend(marker.position as google.maps.LatLng)
        return marker
    })

    mapElement.fitBounds(bounds)

    if (mapMarkers.length === 1) {
        mapElement.setZoom(17)
    }
}

function setMarker(data: GoogleMapsMarker, i: number): google.maps.marker.AdvancedMarkerElement {
    const pin = new PinElement({
        scale: 1,
        glyphColor: '#FFFFFF',
        background: '#1B411E',
        borderColor: '#000000',
    })

    const marker = new AdvancedMarkerElement({
        position: data.position,
        map: mapElement,
        content: pin.element,
        gmpClickable: true,
    })

    marker.addListener('click', () => {
        mapInfoWindow.close()
        mapInfoWindow.setContent(windowInfoElements.value[i]?.$el as HTMLElement)
        mapInfoWindow.open(marker.map, marker)
        data.onClick()
    })

    return marker
}
</script>

<template>
    <div>
        <div
            :id="mapElId"
            style="height: 100%;"
        />
        <div v-show="false">
            <component
                :is="m.infoWindow.component"
                v-for="(m, index) in markers"
                :id="`${mapElId}-window-${index}`"
                :key="index"
                ref="windowInfoElements"
                v-bind="m.infoWindow.props"
            />
        </div>
    </div>
</template>
